import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { STORAGE } from '../../constants';
import { LocalStorageService } from '../../services/local-storage.service';

const STORAGE_KEY = '2fa-notice-dismissed-apr6-2026';
const ENFORCE_DATE = new Date('2026-04-06T00:00:00');

function isDismissed() {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

function isExpired() {
  return new Date() >= ENFORCE_DATE;
}

function checkShouldShow() {
  if (isDismissed() || isExpired()) return false;
  const session = LocalStorageService.get(STORAGE.SESSION);
  if (!session) return false;
  if (session.is_active_2FA === true) return false;
  const isSuperAdmin = session.profile === 'ROOT';
  const isVoicesEmail = session.email?.endsWith('@voices.com.mx') ?? false;
  return isSuperAdmin || isVoicesEmail;
}

function getIs2FAActive() {
  const session = LocalStorageService.get(STORAGE.SESSION);
  return session?.is_active_2FA === true;
}

export default function TwoFANoticeBanner() {
  const [visible, setVisible] = useState(false);
  const [is2FAActive, setIs2FAActive] = useState(false);

  useEffect(() => {
    if (checkShouldShow()) {
      setVisible(true);
      setIs2FAActive(getIs2FAActive());
    }

    const handleSessionUpdated = () => {
      // Al cada login se resetea el dismissed para que el banner siempre aparezca
      localStorage.removeItem(STORAGE_KEY);
      if (checkShouldShow()) {
        setVisible(true);
        setIs2FAActive(getIs2FAActive());
      }
    };

    const handleSessionCleared = () => setVisible(false);
    const handleTwoFAEnabled = () => setVisible(false);
    const handleTwoFADisabled = () => {
      if (checkShouldShow()) setVisible(true);
    };
    window.addEventListener('chatbridge:session-updated', handleSessionUpdated);
    window.addEventListener('chatbridge:session-cleared', handleSessionCleared);
    window.addEventListener('chatbridge:twofa-enabled', handleTwoFAEnabled);
    window.addEventListener('chatbridge:twofa-disabled', handleTwoFADisabled);
    return () => {
      window.removeEventListener('chatbridge:session-updated', handleSessionUpdated);
      window.removeEventListener('chatbridge:session-cleared', handleSessionCleared);
      window.removeEventListener('chatbridge:twofa-enabled', handleTwoFAEnabled);
      window.removeEventListener('chatbridge:twofa-disabled', handleTwoFADisabled);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('has-twofa-banner', visible);
    return () => document.body.classList.remove('has-twofa-banner');
  }, [visible]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const daysLeft = Math.ceil((ENFORCE_DATE.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{ zIndex: 2999, position: 'relative' }}
        >
          <div
            className="d-flex align-items-center gap-3 px-4"
            style={{
              background: '#fffbeb',
              borderBottom: '1px solid #f6c000',
              minHeight: 44,
              paddingTop: 10,
              paddingBottom: 10
            }}
          >
            <span
              className="d-none d-sm-flex align-items-center gap-1 fw-bold rounded px-2 py-1"
              style={{
                background: '#f6c000',
                color: '#1a1200',
                fontSize: '0.7rem',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              <i className="bi bi-shield-lock" style={{ fontSize: '0.75rem', color: '#1a1200' }} />
              AVISO DE SEGURIDAD
            </span>

            <span
              className="flex-grow-1"
              style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 400 }}
            >
              A partir del{' '}
              <span style={{ fontWeight: 700, color: '#92400e' }}>6 de abril de 2026</span>, la
              autenticación en dos pasos (2FA) será obligatoria para todos los usuarios de
              ChatBridge.
              {daysLeft > 0 && (
                <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#6b7280' }}>
                  {daysLeft} día{daysLeft !== 1 ? 's' : ''} restante{daysLeft !== 1 ? 's' : ''}.
                </span>
              )}
            </span>

            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
              {!is2FAActive && (
                <button
                  className="d-none d-md-inline-flex align-items-center gap-1"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent('chatbridge:open-settings-security-2fa', {
                        detail: { openSetup: true }
                      })
                    )
                  }
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: '#92400e',
                    background: 'rgba(146, 64, 14, 0.08)',
                    border: '1px solid rgba(146, 64, 14, 0.25)',
                    borderRadius: 6,
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.background = 'rgba(146, 64, 14, 0.16)';
                    el.style.borderColor = 'rgba(146, 64, 14, 0.45)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.background = 'rgba(146, 64, 14, 0.08)';
                    el.style.borderColor = 'rgba(146, 64, 14, 0.25)';
                  }}
                >
                  Configurar 2FA
                  <i className="bi bi-arrow-right" style={{ fontSize: '0.75rem' }} />
                </button>
              )}

              <button
                onClick={dismiss}
                title="Cerrar aviso"
                aria-label="Cerrar aviso"
                style={{
                  background: 'rgba(146, 64, 14, 0.08)',
                  border: '1px solid rgba(146, 64, 14, 0.2)',
                  cursor: 'pointer',
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  color: '#92400e',
                  fontSize: '0.85rem',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s, border-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  const btn = e.currentTarget as HTMLButtonElement;
                  btn.style.background = 'rgba(146, 64, 14, 0.16)';
                  btn.style.borderColor = 'rgba(146, 64, 14, 0.4)';
                }}
                onMouseLeave={(e) => {
                  const btn = e.currentTarget as HTMLButtonElement;
                  btn.style.background = 'rgba(146, 64, 14, 0.08)';
                  btn.style.borderColor = 'rgba(146, 64, 14, 0.2)';
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
